import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box, TextField, Button, MenuItem, Typography,
    Chip, CircularProgress, Divider, IconButton, Tooltip,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useTablePagination, useAppMeta } from "hooks";
import { exportExcelFile } from "utils/exportExcelFile";
import { useLazyGetStatusLogReportQuery } from "store/services/vendorLeadApi";
import { useLazySearchUsersQuery } from "store/services/userApi";
import { localizeDateTimeShort } from "utils/dateFormats";

const STATUS_OPTIONS = [
    "New", "Qualified", "Negotiation", "QuoteSent", "Won", "Lost",
];

const STATUS_COLOR = {
    New: { bg: "#F5F5F5", color: "#616161" },
    Qualified: { bg: "#E3F2FD", color: "#1565C0" },
    Negotiation: { bg: "#FFF8E1", color: "#F57F17" },
    QuoteSent: { bg: "#E0F7FA", color: "#00695C" },
    Won: { bg: "#E8F5E9", color: "#2E7D32" },
    Lost: { bg: "#FFEBEE", color: "#C62828" },
};

const StatusChip = ({ value }) => {
    if (!value) return <span style={{ color: "#999" }}>—</span>;
    const c = STATUS_COLOR[value] ?? { bg: "#F5F5F5", color: "#616161" };
    return (
        <Chip
            label={value}
            size="small"
            style={{ backgroundColor: c.bg, color: c.color, fontWeight: 600, fontSize: "0.7rem" }}
        />
    );
};

const breadcrumbs = [
     {
      title: "Reports",
      path: "/app/crm/reports/list",
    },
    { title: "Status Log Report" },
];

const VendorLeadStatusLogReport = () => {
    useAppMeta("Lead Status Log Report");

    const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [fromStatus, setFromStatus] = useState("");
    const [toStatus, setToStatus] = useState("");
    const [changedByUser, setChangedByUser] = useState(null);
    const [userOptions, setUserOptions] = useState([]);

    const searchTimerRef = useRef(null);

    const [searchUsers, { isLoading: searchingUsers }] = useLazySearchUsersQuery();

    const handleUserSearch = (_, val) => {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(async () => {
            if (!val) return;
            const res = await searchUsers({ q: val }).unwrap();
            setUserOptions(res || []);
        }, 300);
    };

    const [fetchReport, { data: reportData, isLoading, isFetching }] =
        useLazyGetStatusLogReportQuery();

    const [fetchExport] = useLazyGetStatusLogReportQuery();

    const buildParams = useCallback(
        (extra = {}) => ({
            page: currentPage,
            limit: perPage,
            ...(changedByUser?._id && { changedBy: changedByUser._id }),
            ...(fromStatus && { fromStatus }),
            ...(toStatus && { toStatus }),
            ...(startDate && { startDate }),
            ...(endDate && { endDate }),
            ...extra,
        }),
        [currentPage, perPage, changedByUser, fromStatus, toStatus, startDate, endDate]
    );

    useEffect(() => {
        fetchReport(buildParams());
    }, [fetchReport, buildParams]);

    const handleClearFilters = () => {
        setStartDate("");
        setEndDate("");
        setFromStatus("");
        setToStatus("");
        setChangedByUser(null);
        setUserOptions([]);
    };

    const handleExport = async () => {
        try {
            const res = await fetchExport(buildParams({ exportAll: "true" })).unwrap();
            const exportData = res?.data?.map((row) => ({
                "Lead ID": row.leadId || "N/A",
                "Lead Name": row.fullName || row.companyName || row.contactPersonName || "N/A",
                "Changed By": row.changedBy || "N/A",
                "From Status": row.fromStatus || "—",
                "To Status": row.toStatus || "N/A",
                "Changed At": row.changedAt ? localizeDateTimeShort(row.changedAt) : "N/A",
            }));
            exportExcelFile({
                sheetData: exportData,
                fileName: `Lead_Status_Log_Report_${startDate || "All"}_to_${endDate || "All"}`,
            });
        } catch (err) {
            console.error("Export failed:", err);
        }
    };

    const columns = [
        {
            name: "Lead ID",
            selector: (row) => row?.leadId,
            cell: (row) => (
                <div className="line-clamp-1 font-mono text-xs">
                    {row?.leadId || "N/A"}
                </div>
            ),
            minWidth: "130px",
        },
        {
            name: "Lead Name",
            selector: (row) => row?.fullName ?? row?.companyName ?? row?.contactPersonName,
            cell: (row) => (
                <div className="line-clamp-1">
                    {row?.fullName || row?.companyName || row?.contactPersonName || "N/A"}
                </div>
            ),
            minWidth: "160px",
        },
        {
            name: "Changed By",
            selector: (row) => row?.changedBy,
            cell: (row) => (
                <div className="line-clamp-1">
                    {row?.changedBy || "System"}
                </div>
            ),
            minWidth: "140px",
        },
        {
            name: "From Status",
            selector: (row) => row?.fromStatus,
            cell: (row) => <StatusChip value={row?.fromStatus} />,
            minWidth: "130px",
        },
        {
            name: "To Status",
            selector: (row) => row?.toStatus,
            cell: (row) => <StatusChip value={row?.toStatus} />,
            minWidth: "130px",
        },
        {
            name: "Changed At",
            selector: (row) => row?.changedAt,
            cell: (row) => (
                <div className="line-clamp-1">
                    {row?.changedAt ? localizeDateTimeShort(row.changedAt) : "N/A"}
                </div>
            ),
            minWidth: "170px",
        },
    ];

    const hasFilters = !!(changedByUser || fromStatus || toStatus || startDate || endDate);

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs}>
            <BreadcrumbLayout.Paper>

                {/* ── Filters ── */}
                <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>

                    {/* Changed By */}
                    <BaseAutocomplete
                        label="Changed By"
                        size="small"
                        sx={{ minWidth: 200 }}
                        value={changedByUser ?? null}
                        onChange={(_, data) => setChangedByUser(data)}
                        onInputChange={handleUserSearch}
                        getOptionLabel={(option) => option?.name ?? ""}
                        isOptionEqualToValue={(option, value) => option?._id === value?._id}
                        options={userOptions}
                        loading={searchingUsers}
                        filterOptions={(x) => x}
                    />

                    {/* From Status */}
                    <TextField
                        select
                        label="From Status"
                        value={fromStatus}
                        onChange={(e) => setFromStatus(e.target.value)}
                        size="small"
                        sx={{ minWidth: 150 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {STATUS_OPTIONS.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </TextField>

                    {/* To Status */}
                    <TextField
                        select
                        label="To Status"
                        value={toStatus}
                        onChange={(e) => setToStatus(e.target.value)}
                        size="small"
                        sx={{ minWidth: 150 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {STATUS_OPTIONS.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </TextField>

                    {/* Date Range */}
                    <TextField
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                    />

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleClearFilters}
                        disabled={!hasFilters}
                    >
                        Clear
                    </Button>

                    <Box sx={{ flexGrow: 1 }} />

                    {/* Export */}
                    <Tooltip title="Export to Excel">
                        <span>
                            <IconButton
                                color="primary"
                                onClick={handleExport}
                                disabled={isLoading || isFetching || !reportData?.data?.length}
                                sx={{
                                    border: "1px solid",
                                    borderColor: "primary.main",
                                    borderRadius: 1,
                                    p: "6px",
                                    "&.Mui-disabled": { borderColor: "action.disabled" },
                                }}
                            >
                                <Download fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>

                </Box>

                {/* ── Summary ── */}
                <Box sx={{ px: 2, pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    {(isLoading || isFetching) ? (
                        <CircularProgress size={14} />
                    ) : (
                        <Typography variant="caption" color="text.secondary">
                            <strong>{reportData?.totalSize ?? 0}</strong> total entries
                        </Typography>
                    )}
                </Box>

                <Divider />

                {/* ── Table ── */}
                <AppTable
                    columns={columns}
                    data={reportData?.data ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={reportData?.totalSize ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                />

            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default VendorLeadStatusLogReport;